import { Alert, Badge, Col, Row, Table } from 'reactstrap';
import {
    formatearPuntaje,
    calcularRiesgoResidualInstitucional,
    diagnosticarRiesgoResidual,
} from '../wizard/utils';

// ─── Cálculo de riesgo inherente ────────────────────────────────────────────
function calcularPuntajeRiesgo(cuestionario) {
    const preguntas = cuestionario.filter(
        (p) => p.tipo === 'frequency' && !isNaN(parseFloat(p.peso_default))
    );
    if (!preguntas.length) return null;

    const sumaPesos = preguntas.reduce((s, p) => s + parseFloat(p.peso_default), 0);
    if (!sumaPesos) return null;

    const ponderado = preguntas.reduce((s, p) => {
        return s + ((Number(p.respuesta) - 1) / 4) * parseFloat(p.peso_default);
    }, 0);

    const normalizado = ponderado / sumaPesos;
    return 1 + normalizado * 2; // escala 1–3
}

// ─── Cálculo de eficacia a partir de los mitigantes guardados ────────────────
function calcularPromedioEficacia(mitigantes) {
    const conRespuesta = mitigantes.filter(
        (m) => m.respuesta !== null && m.respuesta !== undefined && m.respuesta !== ''
    );
    if (!conRespuesta.length) return null;
    const suma = conRespuesta.reduce((s, m) => s + Number(m.respuesta), 0);
    return suma / conRespuesta.length;
}

function diagnosticar(puntaje) {
    if (puntaje === null) return null;
    if (puntaje < 1.5) return {
        nivel: 'BAJO', color: 'success',
        descripcion: 'El perfil de riesgo inherente es bajo. La entidad opera con prácticas de baja complejidad y exposición limitada.',
    };
    if (puntaje < 2.2) return {
        nivel: 'MEDIO', color: 'warning',
        descripcion: 'El perfil de riesgo inherente es medio. Se recomienda reforzar controles en los factores de mayor peso.',
    };
    return {
        nivel: 'ALTO', color: 'danger',
        descripcion: 'El perfil de riesgo inherente es alto. Se requieren medidas de debida diligencia reforzada (EDD).',
    };
}

function agruparPorDimension(cuestionario) {
    return cuestionario.reduce((acc, p) => {
        const key = p.dimension || 'sin_dimension';
        if (!acc[key]) acc[key] = [];
        acc[key].push(p);
        return acc;
    }, {});
}

function etiquetaDimension(dimension) {
    if (dimension === 'common') return 'Comunes';
    if (dimension.startsWith('module:')) return dimension.replace('module:', '');
    return dimension;
}

function etiquetaRespuesta(pregunta) {
    const { tipo, respuesta } = pregunta;
    if (respuesta === '' || respuesta === null || respuesta === undefined) return '—';
    if (tipo === 'frequency') {
        const etiquetas = { 1: 'Nunca', 2: 'Raramente', 3: 'Ocasionalmente', 4: 'Frecuentemente', 5: 'Muy frecuente' };
        return `${respuesta} — ${etiquetas[respuesta] || ''}`;
    }
    if (tipo === 'money') return `$${Number(respuesta).toLocaleString('es-MX', { minimumFractionDigits: 2 })}`;
    if (tipo === 'mitigante') return `${respuesta}%`;
    return String(respuesta);
}

function badgePeso(peso) {
    if (peso === 'BLOCKING') return <Badge color="danger">BLOQUEANTE</Badge>;
    if (peso === 'IMPORTANT') return <Badge color="warning" className="text-dark">IMPORTANTE</Badge>;
    const n = parseFloat(peso);
    if (!isNaN(n)) return <Badge color="secondary">{(n * 100).toFixed(0)}%</Badge>;
    return <Badge color="light" className="text-dark">{peso}</Badge>;
}

// ─── Componente principal ────────────────────────────────────────────────────
export default function DashboardRiesgo({ respuesta, onNuevoCuestionario }) {
    const { actividad_vulnerable, cuestionario = [], mitigantes = [], timestamp_utc, usuario } = respuesta;

    const puntaje = calcularPuntajeRiesgo(cuestionario);
    const diagnostico = diagnosticar(puntaje);
    const grupos = agruparPorDimension(cuestionario);

    const promedioEficacia = calcularPromedioEficacia(mitigantes);
    const riesgoResidual = calcularRiesgoResidualInstitucional(puntaje, promedioEficacia);
    const diagnosticoResidual = diagnosticarRiesgoResidual(riesgoResidual);

    const fecha = timestamp_utc
        ? new Date(timestamp_utc).toLocaleString('es-MX', { dateStyle: 'long', timeStyle: 'short' })
        : '—';

    return (
        <div>
            {/* ── Encabezado ── */}
            <div className="d-flex justify-content-between align-items-start mb-3">
                <div>
                    <h5 className="mb-0">Diagnóstico de Riesgo Institucional</h5>
                    <small className="text-muted">
                        Actividad: <strong>{actividad_vulnerable}</strong>
                        {' · '}Respondido el {fecha}
                        {usuario?.nombre && <> por <strong>{usuario.nombre}</strong></>}
                    </small>
                </div>
                {onNuevoCuestionario && (
                    <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        onClick={onNuevoCuestionario}
                    >
                        Actualizar cuestionario
                    </button>
                )}
            </div>

            {/* ── Resultado de riesgo inherente ── */}
            <div className="mb-4 p-3 border rounded">
                <h5>Resultado de riesgo inherente</h5>
                <p className="text-muted small mb-3">
                    Promedio ponderado de las preguntas de frecuencia, escalado en rango 1–3.
                </p>
                <Row className="mb-3">
                    <Col md={6}>
                        <strong>Puntaje de riesgo inherente:</strong>{' '}
                        <span className="fs-5">{formatearPuntaje(puntaje)}</span>
                        <span className="text-muted small"> / 3.00</span>
                    </Col>
                    <Col md={6}>
                        <strong>Preguntas respondidas:</strong>{' '}
                        {cuestionario.length}
                    </Col>
                </Row>
                {diagnostico && (
                    <Alert color={diagnostico.color} className="mb-0">
                        <strong>Diagnóstico — Riesgo {diagnostico.nivel}:</strong>{' '}
                        {diagnostico.descripcion}
                    </Alert>
                )}
            </div>

            {/* ── Resultado de riesgo residual (si hay mitigantes) ── */}
            {riesgoResidual !== null && (
                <div className="mb-4 p-3 border rounded">
                    <h5>Resultado de riesgo residual institucional</h5>
                    <p className="text-muted small mb-3">
                        Riesgo Residual = Riesgo Inherente × (1 − Promedio de eficacia de controles)
                    </p>
                    <Row className="mb-3">
                        <Col md={6}>
                            <strong>Promedio de eficacia de controles:</strong>{' '}
                            {formatearPuntaje(promedioEficacia)}%
                        </Col>
                        <Col md={6}>
                            <strong>Riesgo residual:</strong>{' '}
                            <span className="fs-5">{formatearPuntaje(riesgoResidual)}</span>
                        </Col>
                    </Row>
                    {diagnosticoResidual && (
                        <Alert
                            color={diagnosticoResidual.nivel === 'BAJO' ? 'success' : 'warning'}
                            className="mb-0"
                        >
                            <strong>Diagnóstico — Riesgo residual {diagnosticoResidual.nivel}:</strong>{' '}
                            {diagnosticoResidual.descripcion}
                        </Alert>
                    )}
                </div>
            )}

            {/* ── Detalle de respuestas por dimensión (cuestionario) ── */}
            {Object.entries(grupos).map(([dimension, preguntas]) => (
                <div key={dimension} className="mb-4">
                    <h6 className="border-bottom pb-1 mb-2">
                        {etiquetaDimension(dimension)}
                    </h6>
                    <Table size="sm" bordered responsive>
                        <thead className="table-light">
                            <tr>
                                <th style={{ width: 70 }}>ID</th>
                                <th>Pregunta</th>
                                <th style={{ width: 80 }}>Peso</th>
                                <th style={{ width: 200 }}>Respuesta</th>
                            </tr>
                        </thead>
                        <tbody>
                            {preguntas.map((p) => (
                                <tr key={p.id}>
                                    <td className="text-muted">{p.id}</td>
                                    <td>{p.pregunta}</td>
                                    <td>{badgePeso(p?.peso_default)}</td>
                                    <td>{etiquetaRespuesta(p)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>
            ))}

            {/* ── Detalle de mitigantes guardados (solo lectura) ── */}
            {mitigantes.length > 0 && (
                <div className="mb-4">
                    <h6 className="border-bottom pb-1 mb-2">Mitigantes y controles internos</h6>
                    <Table size="sm" bordered responsive>
                        <thead className="table-light">
                            <tr>
                                <th>Control</th>
                                <th style={{ width: 120 }}>Eficacia</th>
                            </tr>
                        </thead>
                        <tbody>
                            {mitigantes.map((m) => (
                                <tr key={m.id}>
                                    <td>{m.pregunta}</td>
                                    <td className="text-center">
                                        <Badge color={
                                            Number(m.respuesta) === 100 ? 'success'
                                            : Number(m.respuesta) === 50 ? 'warning'
                                            : 'danger'
                                        }>
                                            {m.respuesta !== null && m.respuesta !== undefined ? `${m.respuesta}%` : '—'}
                                        </Badge>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>
            )}
        </div>
    );
}
